import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, Volume2, VolumeX } from "lucide-react";

const isVideo = (src) => /\.(mp4|webm|mov)(\?.*)?$/i.test(String(src || ""));

function MascotVideo({ src, alt, poster }) {
  const [muted, setMuted] = useState(true);
  const ref = useRef(null);
  const visRef = useRef(false);
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const io = new IntersectionObserver(([e]) => {
      visRef.current = e.isIntersecting;
      if (e.isIntersecting) v.play().catch(() => {});
      else v.pause();
    }, { threshold: 0.25 });
    io.observe(v);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    const v = ref.current;
    if (v) { v.muted = muted; if (visRef.current) v.play().catch(() => {}); }
  }, [muted]);
  return (
    <div className="relative h-full aspect-square">
      <video ref={ref} src={src} muted loop playsInline preload="metadata" aria-label={alt}
        poster={poster || undefined} data-testid="mascot-video"
        className="w-full h-full object-cover rounded-3xl border border-white/10 shadow-2xl shadow-black/60" />
      <button type="button" onClick={() => setMuted((m) => !m)} data-testid="mascot-video-sound"
        aria-label={muted ? "Unmute mascot video" : "Mute mascot video"}
        className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/70 backdrop-blur border border-white/20 flex items-center justify-center text-white hover:bg-black/90 transition-colors">
        {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
      </button>
    </div>
  );
}

function useTyped(text, start, speed = 16) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start) return undefined;
    setN(0);
    const id = setInterval(() => setN((x) => (x >= text.length ? (clearInterval(id), x) : x + 1)), speed);
    return () => clearInterval(id);
  }, [text, start, speed]);
  return { shown: text.slice(0, n), done: n >= text.length };
}

const TAILS = {
  left: "-left-1.5 top-8",
  right: "-right-1.5 top-8",
  bottom: "left-10 -bottom-1.5",
  "bottom-right": "right-12 -bottom-1.5",
};

export function SpeechBubble({ text, tail = "left", className = "", testid }) {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setStart(true); io.disconnect(); } }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const { shown, done } = useTyped(text || "", start);
  return (
    <div ref={ref} data-testid={testid} className={`relative bg-white text-zinc-900 rounded-2xl px-5 py-4 shadow-2xl shadow-black/50 ${className}`}>
      <p className="text-sm md:text-[15px] leading-relaxed font-medium whitespace-pre-line min-h-[1.25rem]">
        {shown}
        {!done && <span className="inline-block w-[2px] h-4 bg-rose-600 ml-0.5 animate-pulse align-middle" />}
      </p>
      <span aria-hidden="true" className={`absolute w-4 h-4 bg-white rotate-45 ${TAILS[tail] || TAILS.left}`} />
    </div>
  );
}

export function MascotFigure({ mascot, pose, override, className = "", testid, float = true }) {
  const src = override || mascot?.poses?.[pose] || mascot?.poses?.[mascot?.hero_pose] || Object.values(mascot?.poses || {})[0];
  if (!src) return null;
  const alt = `${mascot?.name || "Mascot"} — ${mascot?.species || ""}`;
  return (
    <motion.div initial={{ opacity: 0, y: 44, scale: 0.72 }} whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-40px" }} transition={{ type: "spring", bounce: 0.42, duration: 0.9 }}
      className={className} data-testid={testid}>
      {isVideo(src) ? (
        <MascotVideo src={src} alt={alt}
          poster={Object.values(mascot?.poses || {}).find((p) => p && !isVideo(p))} />
      ) : (
        <motion.img src={src} alt={alt} loading="lazy"
          animate={float ? { y: [0, -8, 0] } : undefined}
          transition={float ? { repeat: Infinity, duration: 4.5, ease: "easeInOut" } : undefined}
          className="w-full h-full object-contain drop-shadow-[0_28px_44px_rgba(0,0,0,0.65)]" />
      )}
    </motion.div>
  );
}

function NamePlate({ mascot }) {
  return (
    <div className="flex flex-col items-center gap-2" data-testid="mascot-nameplate">
      <p className="font-display font-extrabold text-lg">
        {mascot.name} <span className="text-zinc-400 font-bold text-sm">· {mascot.species}</span>
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        {(mascot.traits || []).map((t) => (
          <span key={t} className="text-[11px] font-bold uppercase tracking-wider text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-full px-3 py-1">{t}</span>
        ))}
      </div>
    </div>
  );
}

export function MascotHero({ hero, mascot, track, slug }) {
  const heroMedia = mascot.hero_media || mascot.poses?.[mascot.hero_pose];
  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid={`sales-hero-${slug}`}>
      <div className="absolute inset-0">
        {hero.image && <img src={hero.image} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-20" fetchPriority="high" />}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/70 via-[#0a0a0a]/85 to-[#0a0a0a]" />
        <div className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-rose-500/15 blur-[140px]" aria-hidden="true" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-teal-500/10 blur-[120px]" aria-hidden="true" />
      </div>
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-32 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          {hero.badge_text && (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-full px-4 py-1.5 mb-5">
              <Sparkles size={12} /> {hero.badge_text}
            </span>
          )}
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{hero.heading}</h1>
          <p className="text-zinc-300 text-base md:text-lg mt-5 max-w-xl">{hero.subheading}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            {hero.cta_primary?.label && (
              <Link to={hero.cta_primary.href || "/"} data-testid="hero-cta-primary"
                onClick={() => track("cta_click", { target: hero.cta_primary.href, meta: hero.cta_primary.label })}
                className="bg-rose-500 hover:bg-rose-600 text-white font-bold px-7 py-3.5 rounded-full text-sm transition-transform hover:scale-[1.03] shadow-xl shadow-rose-500/25">
                {hero.cta_primary.label}
              </Link>
            )}
            {hero.cta_secondary?.label && (
              <Link to={hero.cta_secondary.href || "/"} data-testid="hero-cta-secondary"
                onClick={() => track("cta_click", { target: hero.cta_secondary.href, meta: hero.cta_secondary.label })}
                className="border border-white/25 hover:border-white/60 text-white font-bold px-7 py-3.5 rounded-full text-sm transition-colors backdrop-blur">
                {hero.cta_secondary.label}
              </Link>
            )}
          </div>
        </motion.div>
        <div className="flex flex-col items-center gap-5" data-testid="mascot-hero-figure">
          {mascot.greeting && (
            <SpeechBubble text={mascot.greeting} tail="bottom" className="max-w-md" testid="mascot-hero-bubble" />
          )}
          <MascotFigure mascot={mascot} pose={mascot.hero_pose} override={heroMedia} className="h-[300px] sm:h-[380px] lg:h-[440px]" />
          <NamePlate mascot={mascot} />
        </div>
      </div>
    </section>
  );
}

export function MascotSays({ s, mascot }) {
  if (!mascot) return null;
  const flip = s.align === "right";
  return (
    <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20 relative overflow-hidden" data-testid={`mascot-says-${s.id}`}>
      <div className={`absolute top-1/2 -translate-y-1/2 w-[380px] h-[380px] rounded-full bg-rose-500/[0.07] blur-[110px] ${flip ? "right-0" : "left-0"}`} aria-hidden="true" />
      <div className={`relative max-w-5xl mx-auto flex flex-col items-center gap-8 md:gap-12 ${flip ? "sm:flex-row-reverse" : "sm:flex-row"}`}>
        <MascotFigure mascot={mascot} pose={s.pose} override={s.media} className="h-52 md:h-72 shrink-0" testid={`mascot-pose-${s.id}`} />
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, ease: "easeOut" }} className="flex-1">
          {s.eyebrow && (
            <p className="text-rose-300 text-xs font-bold uppercase tracking-[0.2em] mb-3">{s.eyebrow}</p>
          )}
          {s.heading && <h2 className="font-display font-extrabold text-2xl md:text-3xl mb-5">{s.heading}</h2>}
          {s.says && <SpeechBubble text={s.says} tail={flip ? "right" : "left"} className="max-w-2xl" testid={`mascot-bubble-${s.id}`} />}
          {(s.chips || []).length > 0 && (
            <div className="flex flex-wrap gap-2.5 mt-6">
              {s.chips.map((c) => (
                <span key={c} className="text-sm font-bold border border-rose-500/30 bg-rose-500/[0.07] text-rose-200 rounded-full px-4 py-2">{c}</span>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
